import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { item, track } from '../../features/analytics/analytics';
import type { Product } from '@skytech/shared';
import { OptimizedImage } from '../OptimizedImage';

/** §7.6: keep the primary mounted; a failed or undecoded alternate never replaces it. */
function ImagePreview({
  product,
  priority = false,
  mobileSize,
  listId = 'catalog',
}: {
  product: Product;
  priority?: boolean;
  mobileSize?: 'medium';
  listId?: string;
}): JSX.Element {
  const [primary, secondary] = [...product.images].sort((a, b) => a.displayOrder - b.displayOrder);
  const [requested, setRequested] = useState(false);
  const [ready, setReady] = useState(false);
  const [active, setActive] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(timer.current), []);

  function enter() {
    if (!secondary || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setRequested(true);
      setActive(true);
    }, 140);
  }
  function leave() {
    clearTimeout(timer.current);
    setActive(false);
  }
  const onReady = useCallback(() => setReady(true), []);
  const showAlternate = ready && active;

  return (
    <div
      className="product-photo relative aspect-square overflow-hidden border-b border-frost bg-white"
      onMouseEnter={enter}
      onMouseLeave={leave}
    >
      {primary ? (
        <OptimizedImage
          path={primary.path}
          displayOrder={primary.displayOrder}
          alt={primary.alt ?? product.name}
          width={primary.width}
          height={primary.height}
          mobileSize={mobileSize}
          size="thumbnail"
          aspectRatio="1 / 1"
          priority={priority}
          className="h-full w-full"
          imageClassName="p-2 @[240px]:p-4"
        />
      ) : (
        <div className="flex h-full items-center justify-center text-sm text-neutral">
          Image unavailable
        </div>
      )}
      {requested && secondary && (
        <div
          aria-hidden="true"
          data-alternate-visible={showAlternate}
          className={`pointer-events-none absolute inset-0 bg-white transition-opacity duration-150 motion-reduce:transition-none ${showAlternate ? 'opacity-100' : 'opacity-0'}`}
        >
          <OptimizedImage
            path={secondary.path}
            displayOrder={secondary.displayOrder}
            alt=""
            width={secondary.width}
            height={secondary.height}
            mobileSize={mobileSize}
            size="thumbnail"
            aspectRatio="1 / 1"
            loading="eager"
            onReady={onReady}
            className="h-full w-full"
            imageClassName="p-2 @[240px]:p-4"
          />
        </div>
      )}
      <Link
        to={`/products/${product.slug}`}
        onClick={() => track('select_item', { item_list_id: listId, items: [item(product)] })}
        aria-label={`View ${product.name}`}
        className="absolute inset-0 focus-visible:outline-offset-[-4px]"
      />
      {secondary && (
        <button
          type="button"
          aria-label={`Preview alternate image of ${product.name}`}
          aria-pressed={showAlternate}
          onClick={() => {
            setRequested(true);
            setActive(!active);
          }}
          className="product-preview-toggle absolute bottom-2 left-2 z-10 hidden h-11 w-11 items-center justify-center rounded-full border border-frost bg-white/95 text-navy-700 focus-visible:flex [@media(hover:hover)_and_(pointer:fine)]:flex"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            aria-hidden
          >
            <rect x="7" y="7" width="13" height="13" rx="2" />
            <path
              d="M16 4H6a2 2 0 0 0-2 2v10M10 16l3-3 4 4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="sr-only">{showAlternate ? 'Original view' : 'Alternate view'}</span>
        </button>
      )}
    </div>
  );
}

/** Reset preview state only when the underlying gallery changes, never on a cache refetch. */
export function ProductImagePreview(props: {
  product: Product;
  priority?: boolean;
  mobileSize?: 'medium';
  listId?: string;
}): JSX.Element {
  const identity = [...props.product.images]
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .slice(0, 2)
    .map((i) => `${i.path}:${i.displayOrder}`)
    .join('|');
  return <ImagePreview key={identity} {...props} />;
}
